import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, BookOpen, ChevronRight, Search, Filter } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const accountTypes = [
  { value: 'asset', label: 'Attività', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { value: 'liability', label: 'Passività', color: 'bg-[#ff9787]/20 text-[#ff9787] border-[#ff9787]/30' },
  { value: 'equity', label: 'Patrimonio', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 'revenue', label: 'Ricavi', color: 'bg-primary/20 text-primary border-primary/30' },
  { value: 'expense', label: 'Costi', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
];

const getTypeInfo = (type) => accountTypes.find(t => t.value === type) || accountTypes[0];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(value);
};

const AccountsPage = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    type: 'asset',
    parent_code: '',
    description: ''
  });

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setAccounts(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei conti');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        code: account.code,
        name: account.name,
        type: account.type,
        parent_code: account.parent_code || '',
        description: account.description || ''
      });
    } else {
      setEditingAccount(null);
      setFormData({
        code: '',
        name: '',
        type: 'asset',
        parent_code: '',
        description: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.name) {
      toast.error('Codice e nome sono obbligatori');
      return;
    }

    try {
      if (editingAccount) {
        await axios.put(`${API}/accounts/${editingAccount.id}`, formData);
        toast.success('Conto aggiornato');
      } else {
        await axios.post(`${API}/accounts`, formData);
        toast.success('Conto creato');
      }
      setDialogOpen(false);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`Eliminare il conto ${account.code} - ${account.name}?`)) return;

    try {
      await axios.delete(`${API}/accounts/${account.id}`);
      toast.success('Conto eliminato');
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const filteredAccounts = accounts.filter(acc => {
    const matchesSearch = 
      acc.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      acc.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || acc.type === filterType;
    return matchesSearch && matchesType;
  });

  // Group accounts by type
  const groupedAccounts = accountTypes.reduce((groups, type) => {
    groups[type.value] = filteredAccounts.filter(acc => acc.type === type.value);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="accounts-loading">
        <Skeleton className="h-10 w-64" />
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="accounts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Piano dei Conti</h1>
          <p className="text-muted-foreground mt-1">{accounts.length} conti registrati</p>
        </div>
        {canEdit && (
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 btn-primary-glow"
            data-testid="add-account-btn"
          >
            <Plus size={18} className="mr-2" />
            Nuovo Conto
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input 
                placeholder="Cerca per codice o nome..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-background"
                data-testid="search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-48 bg-background" data-testid="filter-type">
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Filtra per tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                {accountTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Accounts List */}
      {filteredAccounts.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <BookOpen size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">Nessun conto trovato</p>
            {canEdit && (
              <Button 
                variant="link" 
                className="text-primary mt-2"
                onClick={() => handleOpenDialog()}
              >
                Crea il primo conto
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {accountTypes.map(type => {
            const typeAccounts = groupedAccounts[type.value];
            if (typeAccounts.length === 0 && filterType !== 'all' && filterType !== type.value) return null;
            if (typeAccounts.length === 0) return null;
            
            return (
              <Card key={type.value} className="bg-card border-border" data-testid={`account-group-${type.value}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={type.color}>
                        {type.label}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {typeAccounts.length} {typeAccounts.length === 1 ? 'conto' : 'conti'}
                      </span>
                    </div>
                    <span className="font-mono text-sm text-foreground">
                      {formatCurrency(typeAccounts.reduce((sum, a) => sum + a.balance, 0))}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {typeAccounts.map(account => (
                      <div 
                        key={account.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border hover:border-border/80 transition-colors group"
                        data-testid={`account-row-${account.code}`}
                      >
                        <div className="flex items-center gap-4">
                          <span className="font-mono text-sm text-primary w-16">{account.code}</span>
                          <div>
                            <p className="font-medium text-foreground">{account.name}</p>
                            {account.description && (
                              <p className="text-xs text-muted-foreground">{account.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-mono text-sm text-foreground">
                            {formatCurrency(account.balance)}
                          </span>
                          {canEdit && (
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleOpenDialog(account)}
                                data-testid={`edit-account-${account.code}`}
                              >
                                <Pencil size={14} />
                              </Button>
                              {user?.role === 'admin' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleDelete(account)}
                                  className="text-destructive hover:text-destructive"
                                  data-testid={`delete-account-${account.code}`}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="account-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingAccount ? 'Modifica Conto' : 'Nuovo Conto'}
            </DialogTitle>
            <DialogDescription>
              {editingAccount ? 'Modifica i dettagli del conto' : 'Inserisci i dati del nuovo conto'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Codice</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  placeholder="es. 1100"
                  className="bg-background"
                  data-testid="account-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Tipo</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData({...formData, type: v})}
                >
                  <SelectTrigger className="bg-background" data-testid="account-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {accountTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="es. Cassa e Banche"
                className="bg-background"
                data-testid="account-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parent_code">Conto Padre (opzionale)</Label>
              <Select 
                value={formData.parent_code || "none"} 
                onValueChange={(v) => setFormData({...formData, parent_code: v === "none" ? "" : v})}
              >
                <SelectTrigger className="bg-background" data-testid="account-parent-select">
                  <SelectValue placeholder="Seleziona conto padre" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {accounts
                    .filter(a => a.id !== editingAccount?.id)
                    .map(acc => (
                      <SelectItem key={acc.code} value={acc.code}>
                        {acc.code} - {acc.name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrizione (opzionale)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descrizione del conto"
                className="bg-background"
                data-testid="account-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSubmit}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="save-account-btn"
            >
              {editingAccount ? 'Salva' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsPage;
